import React, { useState, useEffect } from 'react';
import { SectionGuide } from '../../components/pas/SectionGuide';
import { CreditAgreementButton } from '../../components/pas/CreditAgreementButton';
import { useNavigate } from 'react-router-dom';
import { usePASClientAuth } from '../../context/PASClientAuthContext';
import { TaxIdPrompt } from '../../components/pas/TaxIdPrompt';
import { HedgeFundSubscriber } from '../../components/pas/HedgeFundSubscriber';
import { MyMembership } from '../../components/pas/MembershipTiers';
import { AddressPrompt } from '../../components/pas/AddressPrompt';
import { CardBuyPanel } from '../../components/pas/CardBuyPanel';
import { WhatsNewPanel } from '../../components/pas/WhatsNewPanel';
import { Crown } from 'lucide-react';
import axios from 'axios';
import { NonFdicNotice } from '../../components/NonFdicNotice';
import { 
  Wallet, Plus, Send, FileCheck, History, RefreshCw, LogOut,
  Coins, DollarSign, ArrowUpRight, ArrowDownLeft, Copy, Check,
  TrendingUp, CreditCard, Receipt, ChevronRight, AlertCircle, FileText, Store, ArrowLeftRight, Landmark
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PASDashboard = () => {
  const navigate = useNavigate();
  const { pasClient, token, logout, refreshClient, loading: authLoading } = usePASClientAuth();
  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'wallet');
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  // Purchase modal state
  const [showPurchase, setShowPurchase] = useState(false);
  const [purchaseAmount, setPurchaseAmount] = useState(10);
  const [purchasing, setPurchasing] = useState(false);

  // Transfer modal state
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferData, setTransferData] = useState({ recipient: '', amount: '', memo: '' });
  const [transferring, setTransferring] = useState(false);

  useEffect(() => {
    if (!authLoading && !pasClient) {
      navigate('/pas/login');
    }
  }, [authLoading, pasClient, navigate]);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [walletRes, txRes, checksRes] = await Promise.all([
        axios.get(`${API}/pas-auth/wallet`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/pas-auth/transactions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/crypto-check/list`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setWallet(walletRes.data);
      setTransactions(txRes.data.transactions || []);
      setChecks(checksRes.data.checks || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const copyWalletAddress = () => {
    navigator.clipboard.writeText(wallet?.wallet_address || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePurchase = async () => {
    setPurchasing(true);
    try {
      const response = await axios.post(
        `${API}/pas-auth/purchase/create-checkout`,
        { usd_amount: purchaseAmount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Purchase failed');
    } finally {
      setPurchasing(false);
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    setTransferring(true);
    try {
      await axios.post(
        `${API}/pas-auth/transfer`,
        {
          recipient_wallet: transferData.recipient,
          beth_amount: parseFloat(transferData.amount),
          memo: transferData.memo
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowTransfer(false);
      setTransferData({ recipient: '', amount: '', memo: '' });
      fetchData();
      refreshClient();
    } catch (err) {
      alert(err.response?.data?.detail || 'Transfer failed');
    } finally {
      setTransferring(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/pas');
  };

  if (authLoading || !pasClient) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {!pasClient.has_tax_id && <TaxIdPrompt token={token} onDone={refreshClient} />}
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center">
              <Coins className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">BETHCOIN</h1>
              <p className="text-xs text-emerald-400">EXCHANGE MARKET</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-slate-400 text-sm">Welcome, {pasClient.first_name}</span>
            <button
              onClick={handleLogout}
              className="p-2 text-slate-400 hover:text-white transition-colors"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Wallet Overview Card */}
        <div className="bg-gradient-to-br from-emerald-600 to-cyan-600 rounded-2xl p-6 mb-8 text-white">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <p className="text-emerald-100 text-sm mb-1">Your BETH Balance</p>
              <h2 className="text-4xl font-bold mb-2" data-testid="beth-balance">
                {wallet?.beth_balance?.toLocaleString() || '0'} <span className="text-2xl">BETH</span>
              </h2>
              <p className="text-emerald-100">
                ≈ ${(wallet?.usd_value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD
              </p>
              <div className="flex items-center gap-2 mt-4">
                <code className="bg-white/20 px-3 py-1 rounded text-sm font-mono" data-testid="wallet-address">
                  {wallet?.wallet_address?.slice(0, 12)}...{wallet?.wallet_address?.slice(-8)}
                </code>
                <button 
                  onClick={copyWalletAddress}
                  className="p-1 hover:bg-white/20 rounded transition-colors"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setShowPurchase(true)}
                data-testid="buy-beth-btn"
                className="px-6 py-3 bg-white text-emerald-700 rounded-xl font-semibold flex items-center gap-2 hover:bg-emerald-50 transition-colors"
              >
                <Plus className="w-5 h-5" /> Buy BETH
              </button>
              <button
                onClick={() => setShowTransfer(true)}
                data-testid="transfer-btn"
                className="px-6 py-3 bg-white/20 text-white rounded-xl font-semibold flex items-center gap-2 hover:bg-white/30 transition-colors"
              >
                <Send className="w-5 h-5" /> Transfer
              </button>
              <button
                onClick={() => navigate('/pas/checks/create')}
                data-testid="create-check-btn"
                className="px-6 py-3 bg-white/20 text-white rounded-xl font-semibold flex items-center gap-2 hover:bg-white/30 transition-colors"
              >
                <FileCheck className="w-5 h-5" /> Mint Check
              </button>
              <CreditAgreementButton token={token} navigate={navigate} />
              <button
                onClick={() => navigate('/pas/bank')}
                data-testid="bank-account-btn"
                className="px-6 py-3 bg-white/20 text-white rounded-xl font-semibold flex items-center gap-2 hover:bg-white/30 transition-colors"
              >
                <Landmark className="w-5 h-5" /> Bank Account
              </button>
              <button
                onClick={() => navigate('/pas/banking')}
                data-testid="banking-credit-btn"
                className="px-6 py-3 bg-amber-400 text-slate-900 rounded-xl font-semibold flex items-center gap-2 hover:bg-amber-300 transition-colors"
              >
                <Wallet className="w-5 h-5" /> Banking &amp; Credit
              </button>
              <button
                onClick={() => navigate('/pas/tax-forms')}
                data-testid="tax-forms-btn"
                className="px-6 py-3 bg-white/90 text-slate-900 rounded-xl font-semibold flex items-center gap-2 hover:bg-white transition-colors"
              >
                <FileText className="w-5 h-5" /> Form 8824
              </button>
              <button
                onClick={() => navigate('/pas/exchange-agreements')}
                data-testid="exchange-agreements-btn"
                className="px-6 py-3 bg-emerald-500/90 text-white rounded-xl font-semibold flex items-center gap-2 hover:bg-emerald-500 transition-colors"
              >
                <FileCheck className="w-5 h-5" /> Exchange Contracts
              </button>
              <button
                onClick={() => navigate('/pas/pos')}
                data-testid="pos-btn"
                className="px-6 py-3 bg-amber-500 text-slate-900 rounded-xl font-semibold flex items-center gap-2 hover:bg-amber-400 transition-colors"
              >
                <Store className="w-5 h-5" /> Point of Sale
              </button>
              <button
                onClick={() => navigate('/pas/swap')}
                data-testid="swap-btn"
                className="px-6 py-3 bg-cyan-600 text-white rounded-xl font-semibold flex items-center gap-2 hover:bg-cyan-500 transition-colors"
              >
                <ArrowLeftRight className="w-5 h-5" /> Exchange &amp; Swap
              </button>
              <button
                onClick={() => navigate('/pas/desk')}
                data-testid="desk-btn"
                className="px-6 py-3 bg-emerald-600 text-white rounded-xl font-semibold flex items-center gap-2 hover:bg-emerald-500 transition-colors"
              >
                <Landmark className="w-5 h-5" /> Buy / Cash Out BETH USD
              </button>
            </div>
          </div>
        </div>
        <NonFdicNotice className="mb-8" />
        <SectionGuide id="dashboard" className="mb-8" />

        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 mb-8 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <TrendingUp className="w-6 h-6 text-emerald-400" />
            <div>
              <p className="text-white font-medium">Current Exchange Rate</p>
              <p className="text-slate-400 text-sm" data-testid="dashboard-rate-display">BETH USD Stablecoin · {wallet?.exchange_rate || '1 BETH = $1.00 USD'}</p>
            </div>
          </div>
          <button
            onClick={() => setShowPurchase(true)}
            className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium transition-colors"
          >
            Buy Now
          </button>
        </div>

        <WhatsNewPanel token={token} onTab={setActiveTab} />
        <MyMembership token={token} />
        <AddressPrompt token={token} prefix="dashboard-address" />

        {/* Tabs */}
        <div className="flex gap-2 sm:gap-4 mb-6 border-b border-slate-700 pb-4 overflow-x-auto">
          {[
            { id: 'wallet', label: 'Wallet', icon: <Wallet className="w-5 h-5" /> },
            { id: 'transactions', label: 'Transactions', icon: <History className="w-5 h-5" /> },
            { id: 'checks', label: 'My Checks', icon: <FileCheck className="w-5 h-5" /> },
            { id: 'hedge-fund', label: 'Hedge Fund', icon: <Crown className="w-5 h-5" /> },
            { id: 'buy-card', label: 'Buy with Card', icon: <CreditCard className="w-5 h-5" /> }
          ].map(tab => (
            <button
              key={tab.id}
              data-testid={`pas-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-emerald-500 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin mx-auto mb-4" />
            <p className="text-slate-400">Loading...</p>
          </div>
        ) : (
          <>
            {/* Wallet Tab */}
            {activeTab === 'hedge-fund' && <HedgeFundSubscriber token={token} />}
            {activeTab === 'buy-card' && <CardBuyPanel token={token} />}
            {activeTab === 'wallet' && (
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm">Total Purchased</p>
                      <p className="text-xl font-bold text-white">{wallet?.total_purchased?.toLocaleString() || 0} BETH</p>
                    </div>
                  </div>
                </div>
                <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                      <ArrowDownLeft className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm">Received</p>
                      <p className="text-xl font-bold text-white">{wallet?.total_transferred_in?.toLocaleString() || 0} BETH</p>
                    </div>
                  </div>
                </div>
                <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                      <ArrowUpRight className="w-5 h-5 text-orange-400" />
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm">Sent</p>
                      <p className="text-xl font-bold text-white">{wallet?.total_transferred_out?.toLocaleString() || 0} BETH</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Transactions Tab */}
            {activeTab === 'transactions' && (
              <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
                {transactions.length === 0 ? (
                  <div className="text-center py-12">
                    <Receipt className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">No transactions yet</p>
                    <button
                      onClick={() => setShowPurchase(true)}
                      className="mt-4 px-4 py-2 bg-emerald-500 text-white rounded-lg font-medium"
                    >
                      Buy Your First BETH
                    </button>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-700">
                    {transactions.map((tx, idx) => (
                      <div key={idx} className="p-4 flex items-center justify-between hover:bg-slate-700/50">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            tx.type === 'purchase' ? 'bg-emerald-500/20' :
                            tx.type === 'transfer' ? 'bg-cyan-500/20' : 'bg-orange-500/20'
                          }`}>
                            {tx.type === 'purchase' && <Plus className="w-5 h-5 text-emerald-400" />}
                            {tx.type === 'transfer' && <Send className="w-5 h-5 text-cyan-400" />}
                            {tx.type === 'check_issued' && <FileCheck className="w-5 h-5 text-orange-400" />}
                          </div>
                          <div>
                            <p className="text-white font-medium capitalize">{tx.type.replace('_', ' ')}</p>
                            <p className="text-slate-400 text-sm">
                              {new Date(tx.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold ${
                            tx.type === 'purchase' || tx.recipient_client_id === pasClient?.client_id
                              ? 'text-emerald-400' : 'text-orange-400'
                          }`}>
                            {tx.type === 'purchase' || tx.recipient_client_id === pasClient?.client_id ? '+' : '-'}
                            {tx.beth_amount} BETH
                          </p>
                          <p className="text-slate-400 text-sm">${tx.usd_value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Checks Tab */}
            {activeTab === 'checks' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold text-white">Your Checks</h3>
                  <button
                    onClick={() => navigate('/pas/checks/create')}
                    className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium flex items-center gap-2"
                  >
                    <Plus className="w-5 h-5" /> Write New Check
                  </button>
                </div>

                {checks.length === 0 ? (
                  <div className="bg-slate-800 border border-slate-700 rounded-xl p-12 text-center">
                    <FileCheck className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                    <h4 className="text-xl font-bold text-white mb-2">No Checks Yet</h4>
                    <p className="text-slate-400 mb-6">You are the banker now. Write your first crypto check!</p>
                    <button
                      onClick={() => navigate('/pas/checks/create')}
                      className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-semibold"
                    >
                      Write Your First Check
                    </button>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {checks.map((check, idx) => (
                      <div 
                        key={idx}
                        onClick={() => navigate(`/pas/checks/${check.check_number}`)}
                        className="bg-slate-800 border border-slate-700 rounded-xl p-6 cursor-pointer hover:border-emerald-500/50 transition-colors"
                      >
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <p className="text-emerald-400 font-mono text-sm">{check.check_number}</p>
                            <p className="text-white font-bold text-lg mt-1">
                              {check.beth_amount} BETH <span className="text-slate-400 text-sm">(${check.usd_value})</span>
                            </p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            check.status === 'issued' ? 'bg-emerald-500/20 text-emerald-400' :
                            check.voided ? 'bg-red-500/20 text-red-400' : 'bg-slate-700 text-slate-400'
                          }`}>
                            {check.voided ? 'Voided' : check.status}
                          </span>
                        </div>
                        <p className="text-slate-400 text-sm">Pay to: {check.payee_name}</p>
                        <p className="text-slate-500 text-xs mt-2">
                          {new Date(check.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Purchase Modal */}
      {showPurchase && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-white mb-4">Buy BETH</h3>
            <p className="text-slate-400 mb-6">Exchange Rate: 1 BETH = $1.00 USD</p>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-300 mb-2">Amount (USD)</label>
              <div className="relative">
                <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="number"
                  min="1"
                  max="10000"
                  value={purchaseAmount}
                  onChange={(e) => setPurchaseAmount(Number(e.target.value))}
                  data-testid="buy-beth-amount-input"
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg pl-12 pr-4 py-3 text-white focus:border-emerald-500 outline-none"
                />
              </div>
              <p className="text-emerald-400 mt-2">
                You will receive: <strong>{Number(purchaseAmount).toLocaleString()} BETH</strong>
              </p>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => setShowPurchase(false)}
                data-testid="buy-beth-cancel-btn"
                className="flex-1 border border-slate-600 text-white rounded-lg py-3 font-medium hover:bg-slate-700"
              >
                Cancel
              </button>
              <button
                onClick={handlePurchase}
                data-testid="buy-beth-pay-btn"
                disabled={purchasing || purchaseAmount < 1}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg py-3 font-medium disabled:opacity-50"
              >
                {purchasing ? 'Processing...' : `Pay $${purchaseAmount}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Modal */}
      {showTransfer && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-white mb-4">Transfer BETH</h3>
            
            <form onSubmit={handleTransfer} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Recipient (Email or Wallet Address)
                </label>
                <input
                  type="text"
                  value={transferData.recipient}
                  onChange={(e) => setTransferData({ ...transferData, recipient: e.target.value })}
                  placeholder="recipient@email.com"
                  required
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white focus:border-emerald-500 outline-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Amount (BETH)</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  max={wallet?.beth_balance || 0}
                  value={transferData.amount}
                  onChange={(e) => setTransferData({ ...transferData, amount: e.target.value })}
                  placeholder="100"
                  required
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white focus:border-emerald-500 outline-none"
                />
                <p className="text-slate-400 text-sm mt-1">
                  Available: {wallet?.beth_balance?.toLocaleString() || 0} BETH
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Memo (Optional)</label>
                <input
                  type="text"
                  value={transferData.memo}
                  onChange={(e) => setTransferData({ ...transferData, memo: e.target.value })}
                  placeholder="Payment for..."
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white focus:border-emerald-500 outline-none"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowTransfer(false)}
                  className="flex-1 border border-slate-600 text-white rounded-lg py-3 font-medium hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={transferring}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg py-3 font-medium disabled:opacity-50"
                >
                  {transferring ? 'Sending...' : 'Send BETH'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PASDashboard;
